/**
 * Fechas y horas de la plataforma, en un solo lugar.
 *
 * ---------------------------------------------------------------------------
 * POR QUÉ HACÍA FALTA
 * ---------------------------------------------------------------------------
 * El backend no guarda todas las fechas de la misma forma, y no se puede
 * cambiar sin romper el histórico:
 *
 *   A) Columnas DATETIME/TIMESTAMP (último acceso, puntos, bitácora, subidas,
 *      encuestas). Llegan como texto "2026-09-21 14:20:00", SIN ninguna marca
 *      de zona, y desde ahora siempre en hora de Colombia (ver
 *      backend/config/zonaHoraria.js).
 *
 *   B) Publicaciones y comentarios, que guardan la fecha como texto ISO en
 *      UTC: "2026-09-21T19:20:00.000Z".
 *
 * En pantalla se mezclaban tres maneras distintas de leerlas, y dos de ellas
 * dependían del navegador:
 *
 *   - `new Date("2026-09-21 14:20:00")` funciona en Chrome (lo toma como hora
 *     local) pero devuelve "Invalid Date" en Firefox y en Safari.
 *   - Tomar la hora local del navegador hace que la misma fila se vea a
 *     distinta hora según desde dónde se mire.
 *
 * ---------------------------------------------------------------------------
 * LA REGLA
 * ---------------------------------------------------------------------------
 * Se lee cada formato con su propio criterio y se PINTA SIEMPRE en hora de
 * Colombia, sin importar dónde esté el servidor ni dónde esté la persona.
 */

/** Misma zona que usa el backend. */
export const ZONA = 'America/Bogota';

/** Desfase de Colombia. No tiene horario de verano, así que es constante. */
const DESFASE = '-05:00';

const SOLO_FECHA = /^(\d{4})-(\d{2})-(\d{2})$/;
const FECHA_Y_HORA = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/;
const TRAE_ZONA = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * Convierte cualquiera de los formatos del backend en un Date real.
 * Devuelve null si el valor no se puede leer, para que quien llame decida
 * qué pintar en vez de mostrar "Invalid Date" o la fecha de hoy.
 *
 * @param {string|number|Date} valor
 * @returns {Date|null}
 */
export const aInstante = (valor) => {
  if (!valor) return null;
  if (valor instanceof Date) return isNaN(valor.getTime()) ? null : valor;
  if (typeof valor === 'number') {
    const d = new Date(valor);
    return isNaN(d.getTime()) ? null : d;
  }

  const texto = String(valor).trim();
  if (!texto) return null;

  // Fecha de calendario sin hora (nacimiento, ingreso): NO lleva zona. Se
  // arma a mediodía para que ningún desfase la corra al día anterior, que es
  // el error clásico de `new Date("2000-02-08")`.
  const soloFecha = texto.match(SOLO_FECHA);
  if (soloFecha) {
    const [, a, m, d] = soloFecha;
    return new Date(Number(a), Number(m) - 1, Number(d), 12, 0, 0);
  }

  // Ya trae zona (las publicaciones y comentarios, que van en UTC con "Z"):
  // es un instante exacto, se respeta tal cual.
  if (TRAE_ZONA.test(texto)) {
    const d = new Date(texto);
    return isNaN(d.getTime()) ? null : d;
  }

  // DATETIME de la base de datos: hora de Colombia sin marca de zona. Se le
  // pone el desfase explícito para que no dependa del navegador.
  const conHora = texto.match(FECHA_Y_HORA);
  if (conHora) {
    const [, a, m, d, h, mi, s] = conHora;
    const iso = `${a}-${m}-${d}T${h}:${mi}:${s || '00'}${DESFASE}`;
    const fecha = new Date(iso);
    return isNaN(fecha.getTime()) ? null : fecha;
  }

  const suelta = new Date(texto);
  return isNaN(suelta.getTime()) ? null : suelta;
};

/** Milisegundos para ordenar. Lo ilegible se manda al final, no al principio. */
export const enMilisegundos = (valor) => {
  const d = aInstante(valor);
  return d ? d.getTime() : 0;
};

const formatear = (valor, opciones, vacio) => {
  const d = aInstante(valor);
  if (!d) return vacio;
  return d.toLocaleString('es-CO', { timeZone: ZONA, ...opciones });
};

/** "21 sept 2026" */
export const formatearFecha = (valor, vacio = '—') =>
  formatear(valor, { day: '2-digit', month: 'short', year: 'numeric' }, vacio);

/** "21 sept 2026, 02:20 p. m." */
export const formatearFechaHora = (valor, vacio = '—') =>
  formatear(
    valor,
    { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' },
    vacio
  );

/** "02:20 p. m." */
export const formatearHora = (valor, vacio = '—') =>
  formatear(valor, { hour: '2-digit', minute: '2-digit' }, vacio);

/**
 * "hace 5 minutos". Como ahora la fecha se interpreta bien, ya no puede dar
 * "hace -5 horas" ni "hace 0 segundos" para algo recién publicado.
 */
export const tiempoRelativo = (valor, vacio = '') => {
  const d = aInstante(valor);
  if (!d) return vacio;

  const diff = Date.now() - d.getTime();
  // Un desfase de unos segundos entre el reloj del servidor y el del
  // computador es normal; se muestra "hace un momento" en vez de un futuro.
  if (diff < 0) return 'hace un momento';

  const seg = Math.floor(diff / 1000);
  const min = Math.floor(diff / 60000);
  const hrs = Math.floor(diff / 3600000);
  const dias = Math.floor(diff / 86400000);

  if (dias > 0) return `hace ${dias} día${dias > 1 ? 's' : ''}`;
  if (hrs > 0) return `hace ${hrs} hora${hrs > 1 ? 's' : ''}`;
  if (min > 0) return `hace ${min} minuto${min > 1 ? 's' : ''}`;
  if (seg > 5) return `hace ${seg} segundos`;
  return 'hace un momento';
};
